/**
 * Selection-state API client.
 *
 * Backs the discovery surfaces' "considering / saved / disqualified" toggle.
 * The state document is keyed at the top level by entity type so the same
 * pattern works for companies (today) and positions/classes/markets/etc. later.
 *
 * @typedef {"considering"|"saved"|"disqualified"} SelectionState
 * @typedef {Object<string, Object<string, SelectionState>>} UserSelections
 *   Shape: { [entityType]: { [entityId]: state } }
 */
import axios from 'axios';
import { mockAuthHeaders } from '../utils/mockAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const headers = () => ({ ...mockAuthHeaders() });

/** @returns {Promise<UserSelections>} */
export async function getSelections() {
  const res = await axios.get(`${API_URL}/api/selections`, { headers: headers() });
  return res.data || {};
}

/**
 * Set or clear one entity's state. Pass `state=null` to cycle back to undefined.
 * @returns {Promise<UserSelections>} the updated full document
 */
export async function patchSelection(entityType, entityId, state) {
  const res = await axios.patch(
    `${API_URL}/api/selections/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    { state: state ?? null },
    { headers: headers() },
  );
  return res.data || {};
}

/** Replace the full selections document (bulk edits). */
export async function replaceSelections(selections) {
  const res = await axios.put(
    `${API_URL}/api/selections`,
    { selections },
    { headers: headers() },
  );
  return res.data || {};
}
